import React, { useEffect, useRef, useState } from "react";

// A right-click-safe Vimeo player.
//
// A plain <iframe src="https://player.vimeo.com/..."> is cross-origin, so
// nothing on our page can reach into it - not the contextmenu blocker in
// App.jsx, not anything else. The browser's native "Save video as" menu
// still works inside it regardless of what we do on our own page. That's
// exactly what was reported: right-click stayed blocked on the sample
// (same-origin, uploaded) video, but not on Vimeo embeds.
//
// The fix: load the official Vimeo Player SDK (https://player.vimeo.com/api/player.js)
// and drive the player over postMessage instead of letting students touch
// the iframe directly. We render our OWN control bar (play/pause, seek,
// volume, fullscreen) and place an invisible "click shield" directly on top
// of the iframe. Because every mouse event now lands on our shield (same
// document, same origin) rather than reaching Vimeo's iframe at all, a
// right-click here is a right-click on OUR page - which the site-wide
// contextmenu blocker (App.jsx) already catches like any other element.
let vimeoSdkPromise = null;
function loadVimeoSdk() {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.Vimeo && window.Vimeo.Player) return Promise.resolve();
  if (vimeoSdkPromise) return vimeoSdkPromise;
  vimeoSdkPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById("vimeo-player-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Vimeo SDK failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.id = "vimeo-player-sdk";
    script.src = "https://player.vimeo.com/api/player.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Vimeo SDK failed to load"));
    document.head.appendChild(script);
  });
  return vimeoSdkPromise;
}

function formatTime(sec) {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function blockContextMenu(e) {
  e.preventDefault();
  return false;
}

export default function VimeoPlayer({ videoUrl }) {
  const holderRef = useRef(null);
  const playerRef = useRef(null);
  const seekingRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [volume, setVolume] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    let destroyed = false;
    setReady(false);
    setError("");
    loadVimeoSdk()
      .then(() => {
        if (destroyed || !holderRef.current) return;
        const player = new window.Vimeo.Player(holderRef.current, {
          // Pass the whole URL (not just a numeric ID) - the SDK parses it
          // itself, including any privacy hash a private/unlisted video's
          // URL carries after the ID (vimeo.com/123456789/abcNDEF987). An
          // ID-only load fails for those videos even though the SDK script
          // itself loaded fine, which is the "Couldn't load this video"
          // case as opposed to "Couldn't load the video player" below.
          url: videoUrl,
          controls: false, // we render our own controls below
          responsive: true,
          dnt: true,
        });
        playerRef.current = player;
        player
          .ready()
          .then(() => {
            if (destroyed) return;
            setReady(true);
            player.getDuration().then((d) => !destroyed && setDuration(d));
            player.getVolume().then((v) => !destroyed && setVolume(v));
          })
          .catch((err) => {
            if (destroyed) return;
            // eslint-disable-next-line no-console
            console.error("Vimeo player error:", err);
            // Vimeo's SDK throws named errors for the common
            // still-can't-load cases - surface the actual reason instead of
            // a generic message, since these two are almost always a Vimeo
            // account/video *setting*, not something wrong with this site:
            //  - PrivacyError: the video's embed privacy is restricted to
            //    specific domains and this site isn't on that list (Vimeo ->
            //    video's Settings -> Distribution -> "Where can this be
            //    embedded?"), or the video needs a privacy hash that wasn't
            //    included in the URL saved here.
            //  - PasswordError: the video requires a password to play.
            if (err && err.name === "PrivacyError") {
              setError(
                "This video's Vimeo privacy settings don't allow it to be embedded here - check the video's " +
                  '"Where can this be embedded?" setting on Vimeo (Settings → Distribution) and make sure this site\'s domain is allowed, or that the video URL saved here includes its privacy hash.'
              );
            } else if (err && err.name === "PasswordError") {
              setError("This video requires a password on Vimeo and can't play here until that's removed.");
            } else {
              setError("Couldn't load this video.");
            }
          });
        player.on("play", () => setPlaying(true));
        player.on("pause", () => setPlaying(false));
        player.on("ended", () => setPlaying(false));
        player.on("timeupdate", (data) => {
          if (!seekingRef.current) setCurrent(data.seconds);
        });
      })
      .catch((err) => {
        if (destroyed) return;
        // eslint-disable-next-line no-console
        console.error("Vimeo SDK load error:", err);
        setError("Couldn't load the video player.");
      });
    return () => {
      destroyed = true;
      if (playerRef.current) {
        playerRef.current.destroy().catch(() => {});
        playerRef.current = null;
      }
    };
  }, [videoUrl]);

  const togglePlay = () => {
    const player = playerRef.current;
    if (!player) return;
    if (playing) player.pause();
    else player.play();
  };

  const seek = (e) => {
    const value = Number(e.target.value);
    setCurrent(value);
    if (playerRef.current) playerRef.current.setCurrentTime(value);
  };

  const changeVolume = (e) => {
    const value = Number(e.target.value);
    setVolume(value);
    if (playerRef.current) playerRef.current.setVolume(value);
  };

  const requestFullscreen = () => {
    if (playerRef.current) playerRef.current.requestFullscreen().catch(() => {});
  };

  return (
    <div className="vimeo-player">
      <div className="vimeo-player-frame">
        <div ref={holderRef} className="vimeo-player-holder" />
        <div
          className="vimeo-click-shield"
          onClick={togglePlay}
          onContextMenu={blockContextMenu}
          title={playing ? "Pause" : "Play"}
        />
        {!ready && !error && <div className="vimeo-loading">Loading video…</div>}
        {error && <div className="vimeo-loading">{error}</div>}
      </div>
      {ready && (
        <div className="vimeo-controls" onContextMenu={blockContextMenu}>
          <button type="button" className="vimeo-btn" onClick={togglePlay} title={playing ? "Pause" : "Play"}>
            {playing ? "⏸" : "▶"}
          </button>
          <span className="vimeo-time">{formatTime(current)}</span>
          <input
            type="range"
            min={0}
            max={duration || 0}
            step={0.1}
            value={current}
            onMouseDown={() => (seekingRef.current = true)}
            onMouseUp={() => (seekingRef.current = false)}
            onChange={seek}
            className="vimeo-seek"
            aria-label="Seek"
          />
          <span className="vimeo-time">{formatTime(duration)}</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={changeVolume}
            className="vimeo-volume"
            aria-label="Volume"
            title="Volume"
          />
          <button type="button" className="vimeo-btn" onClick={requestFullscreen} title="Fullscreen">
            ⛶
          </button>
        </div>
      )}
    </div>
  );
}
